import React from "react";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import ToolsModal from "../clients/Tools/kit/ToolsModal";
import ToolsCheckbox from "../clients/Tools/kit/ToolsCheckbox";
import ToolsSelect from "../clients/Tools/kit/ToolsSelect";
import PlaybookCorrectionsService from "../../services/PlaybookCorrectionsService";
import UtilityService from "../../services/UtilityService";
import UserService from "../../services/UserService";

import "./playbook-corrections-panel.css";

class PlaybookCorrectionsPanel extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			corrections: null,
			isLoading: true,
			loadError: null,
			isExportOpen: false,
			onlyFinal: true,
			leaveOutClients: false,
			leaveOutPeople: false,
			isExporting: false,
			exportError: null,
			lastExport: null,
			canonicalCode: "",
		};
	}

	componentDidMount() {
		this.fetchCorrections();
	}

	fetchCorrections = async () => {
		const { playbookId } = this.props;
		const { canonicalCode } = this.state;
		try {
			const corrections = await PlaybookCorrectionsService.describeCorrections({ playbookId, canonicalCode: canonicalCode || null });
			await this.update({ corrections, isLoading: false, loadError: null });
		} catch (error) {
			await this.update({
				isLoading: false,
				loadError: UtilityService.describeRequestError({ error, fallback: "The corrections could not be loaded." }),
			});
		}
	};

	onChooseLine = async canonicalCode => {
		await this.update({ canonicalCode, lastExport: null });
		await this.fetchCorrections();
	};

	onOpenExport = () => {
		this.update({ isExportOpen: true, exportError: null });
	};

	onCloseExport = () => {
		this.update({ isExportOpen: false });
	};

	onExport = async () => {
		const { playbookId } = this.props;
		const { onlyFinal, leaveOutClients, leaveOutPeople, canonicalCode } = this.state;
		await this.update({ isExporting: true, exportError: null });
		try {
			const result = await PlaybookCorrectionsService.exportDecisions({ playbookId, canonicalCode: canonicalCode || null, onlyFinal, leaveOutClients, leaveOutPeople });
			if (result.rowCount === 0) {
				const emptyReason = onlyFinal
					? "Nothing to export yet: no answer on this playbook has been signed off. Untick the first option to export every recorded judgment."
					: "Nothing to export: this playbook has no recorded judgments.";
				await this.update({ isExporting: false, exportError: emptyReason });
				return;
			}
			UtilityService.downloadFile({ blob: new Blob([result.jsonl], { type: "application/x-ndjson" }), fileName: result.fileName });
			await this.update({ isExporting: false, isExportOpen: false, lastExport: result });
		} catch (error) {
			await this.update({ isExporting: false, exportError: UtilityService.describeRequestError({ error, fallback: "The export did not finish." }) });
		}
	};

	renderPoint = point => {
		const agreement = PlaybookCorrectionsService.describeAgreement({ point });
		return (
			<div key={point.key} className="tk-table__row tk-table__row--body playbook-corrections__row">
				<span className="tk-table__stack">
					<span className="tk-table__primary">{point.question || point.key}</span>
					{point.topChanges.length > 0 && (
						<span className="tk-table__meta">
							Changed most often: {point.topChanges.map(change => `${change.key} (${change.count})`).join(" · ")}
						</span>
					)}
				</span>
				<span className="playbook-corrections__number">{point.recorded}</span>
				<span className="playbook-corrections__number">{point.reviewed}</span>
				<span className="playbook-corrections__agreement">
					<span className={`tk-dot tk-dot--${agreement.tone}`} />
					{agreement.text}
				</span>
				<span className="playbook-corrections__number">{point.changedAnswer}</span>
				<span className="playbook-corrections__number">{point.overturnedByTheBooks}</span>
				<span className="tk-table__meta">{PlaybookCorrectionsService.describeDisagreementReasons({ point })}</span>
			</div>
		);
	};

	renderExportModal() {
		const { isExportOpen, onlyFinal, leaveOutClients, leaveOutPeople, isExporting, exportError, canonicalCode, corrections } = this.state;
		const chosenLine = canonicalCode ? PlaybookCorrectionsService.describeLineChoices({ corrections }).find(choice => choice.value === canonicalCode) : null;
		const footer = (
			<div className="tk-modal-actions">
				<button type="button" className="tk-button tk-button--secondary" onClick={this.onCloseExport}>
					Cancel
				</button>
				<button type="button" className="tk-button tk-button--primary" onClick={this.onExport} disabled={isExporting}>
					<Icon.Download size={14} />
					{isExporting ? "Exporting…" : "Export"}
				</button>
			</div>
		);
		return (
			<ToolsModal open={isExportOpen} onClose={this.onCloseExport} title="Export decision records" width={520} footer={footer}>
				<div className="playbook-corrections__options">
					<ToolsCheckbox
						size="form"
						checked={onlyFinal}
						onChange={() => this.update({ onlyFinal: !onlyFinal })}
						label="Only answers that were signed off or shown by the books"
					/>
					<ToolsCheckbox
						size="form"
						checked={leaveOutClients}
						onChange={() => this.update({ leaveOutClients: !leaveOutClients })}
						label="Leave out client names and ids"
					/>
					<ToolsCheckbox
						size="form"
						checked={leaveOutPeople}
						onChange={() => this.update({ leaveOutPeople: !leaveOutPeople })}
						label="Leave out who decided (their role stays)"
					/>
				</div>
				{chosenLine && <p className="tk-form-help">Only the decisions about {chosenLine.label}.</p>}
				<p className="tk-form-help">
					Everything is recorded in full; these choices only shape this file. Each line says whether it is ready to train on and why not. The export
					is logged with your name.
				</p>
				{exportError && <div className="tk-notice tk-notice--danger">{exportError}</div>}
			</ToolsModal>
		);
	}

	render() {
		const { corrections, isLoading, loadError, lastExport, canonicalCode } = this.state;
		if (isLoading) return <div className="tk-empty">Loading corrections…</div>;
		if (loadError) return <div className="tk-notice tk-notice--danger">{loadError}</div>;
		const canExport = UserService.isAdministrator();
		const isLineFilterShown = corrections.lines.length > 0 || !!canonicalCode;

		return (
			<div className="playbook-corrections">
				{isLineFilterShown && (
					<div className="tk-toolbar">
						<ToolsSelect
							filter
							searchable
							searchPlaceholder="Search statement lines"
							label="Statement line"
							value={canonicalCode}
							options={PlaybookCorrectionsService.describeLineChoices({ corrections })}
							onChange={this.onChooseLine}
						/>
					</div>
				)}
				<div className="playbook-corrections__head">
					<p className="tk-page-intro">{PlaybookCorrectionsService.describeCorrectionsIntro({ corrections })}</p>
					{canExport && corrections.recordCount > 0 && (
						<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={this.onOpenExport}>
							<Icon.Download size={13} />
							Export decisions
						</button>
					)}
				</div>
				{lastExport && <div className="tk-notice">{PlaybookCorrectionsService.describeExportResult({ result: lastExport })}</div>}
				{corrections.points.length > 0 && (
					<div className="tk-table tk-card">
						<div className="tk-table__row tk-table__row--head playbook-corrections__row">
							<span className="tk-table__head-cell">Judgment</span>
							<span className="tk-table__head-cell">Recorded</span>
							<span className="tk-table__head-cell">Reviewed</span>
							<span className="tk-table__head-cell">Agreed</span>
							<span className="tk-table__head-cell">Changed</span>
							<span className="tk-table__head-cell">Overturned</span>
							<span className="tk-table__head-cell">Why people disagreed</span>
						</div>
						{corrections.points.map(this.renderPoint)}
					</div>
				)}
				{this.renderExportModal()}
			</div>
		);
	}
}

export default PlaybookCorrectionsPanel;
